import { useToast } from '@chakra-ui/react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  createContext,
} from 'react';
import { useHistory } from 'react-router-dom';

import { LoanProductGQLType } from 'types/dashboard-core-gqlTypes.generated.d';

import { useLoanProductCreditFacilityGroupContext } from './LoanProductCreditFacilityGroupingProvider';
import { errorToastConfig } from 'packages/common-frontend/constants';

export type LoanProduct = Pick<
  LoanProductGQLType,
  'id' | 'name' | 'slug' | 'currentCreditFacilities'
>;

export type LoanProductForLpCfGroupingContextType = {
  loanProduct: LoanProduct;
  // not a set state dispatch because we have logic surrounding the set state
  setLoanProduct: (newLoanProduct: LoanProduct) => void;
};

export const LoanProductForLpCfGroupingContext = createContext<LoanProductForLpCfGroupingContextType | null>(
  null,
);

/**
 * # ⚠️ WARNING
 * ## To be used with `LoanProductCreditFacilityGroupContext`
 * ## Not to be used on it's own as a LP context!
 */
export const useLoanProductForLpCfGroupingContext = () => {
  const context = useContext(LoanProductForLpCfGroupingContext);
  if (context === null) {
    throw new Error('LoanProductCreditFacilityGroupContext must be present');
  }

  return context;
};

export const LoanProductforLpCFGroupingProvider: React.FC<{
  renderLoading: React.ReactElement;
  children: React.ReactNode;
}> = ({ renderLoading, children }) => {
  const toast = useToast();
  const history = useHistory();
  const { lpToCfEntityGrouping } = useLoanProductCreditFacilityGroupContext();
  const [loanProduct, setLoanProduct] = useState<LoanProduct | null>(
    lpToCfEntityGrouping?.loanProducts[0] ?? null,
  );

  useEffect(() => {
    if (!lpToCfEntityGrouping) {
      setLoanProduct(null);
    }
    const newLoanProductIds = lpToCfEntityGrouping.loanProducts.map(
      ({ id }) => id,
    );
    if (loanProduct && !newLoanProductIds.includes(loanProduct.id)) {
      setLoanProduct(lpToCfEntityGrouping.loanProducts[0]);
    }
  }, [lpToCfEntityGrouping, loanProduct]);

  const handleSetLoanProduct = useCallback(
    (newLoanProduct: LoanProduct) => {
      try {
        // @jendevelops TODO: toast on this error? Better way to handle it?
        if (!lpToCfEntityGrouping) {
          history.push('/assignments');
        }

        const validLpIds = lpToCfEntityGrouping.loanProducts.map((lp) => lp.id);
        if (validLpIds.includes(newLoanProduct.id)) {
          setLoanProduct(newLoanProduct);
        } else {
          throw Error(
            `Invalid loan product [${
              newLoanProduct.name
            }] selected. Only [${validLpIds.join(', ')}] are allowed`,
          );
        }
      } catch (err: any) {
        if (err.message) {
          errorToastConfig.title = err.message;
        }
        errorToastConfig.title = 'Error setting loan product';
        errorToastConfig.id = 'error-setting-loan-product';
        toast(errorToastConfig);
      }
    },
    [lpToCfEntityGrouping, toast, history],
  );

  const context = useMemo(() => {
    if (lpToCfEntityGrouping && loanProduct) {
      return {
        loanProduct,
        setLoanProduct: handleSetLoanProduct,
      };
    }
    return null;
  }, [lpToCfEntityGrouping, handleSetLoanProduct, loanProduct]);

  if (!context) {
    return renderLoading;
  }
  return (
    <LoanProductForLpCfGroupingContext.Provider value={context}>
      {children}
    </LoanProductForLpCfGroupingContext.Provider>
  );
};
